<template src="./index.html" />

<script>
import * as XLSX from 'xlsx'
import {
  BButton, BFormFile, BCard, BCol, BOverlay, BRow, BFormSelect, BPagination
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import {
  showErrorToast, showSuccessToast, showWarningToast, showInfoToast,
} from '@/utils/notification'
import '@core/scss/vue/libs/vue-good-table.scss'
import { Workbook } from '@syncfusion/ej2-excel-export'
import { getUser } from '@/auth/utils'
import { hasPermissionForResource, isSystemAdmin } from '@/utils'
import { ApiCode } from '@/const/api'
import { PermissionCode, ResourceCode } from '@/const/code'
import api from './api'

export default {
  name: 'ImportExaminationScore',
  components: {
    BButton,
    BFormFile,
    BCard,
    BCol,
    BOverlay,
    BRow,
    BFormSelect,
    XLSX,
    VueGoodTable,
    BPagination
  },
  data() {
    return {
      user: getUser(),
      table: {
        fields: [
          {
            field: 'stt', label: 'STT', sortable: false, type: 'string',
          },
          {
            field: 'studentCode', label: 'Mã sinh viên(*)', sortable: false, type: 'string',
          },
          {
            field: 'creditClassCode', label: 'Mã lớp học phần(*)', sortable: false, type: 'string',
          },
          {
            field: 'score', label: 'Điểm thi', sortable: false, type: 'number',
          },
        ],
        items: [],
      },
      tenFile: '',
      fileUpload: null,
      dataExcel: [],
      totalRecords: 0,
      serverParams: {
        page: 1,
        perPage: 10,
      },
      loading: false,
    }
  },
  computed: {
    creatable() {
      return isSystemAdmin() || hasPermissionForResource(PermissionCode.CREATE, ResourceCode.EXAMINATION_ROOM)
    },
  },
  methods: {
    async importExcel(e) {
      const { files } = e.target
      if (!files.length) {
        return
      }
      if (!/\.(xls|xlsx)$/.test(files[0].name.toLowerCase())) {
        showErrorToast(this, 'Vui lòng chọn file Excel (.xls hoặc .xlsx)')
        return
      }
      if (files[0].name) {
        this.tenFile = files[0].name
      }
      this.readFile(files)
    },

    readFile(files) {
      const fileReader = new FileReader()
      fileReader.onload = async ev => {
        try {
          const data = ev.target.result
          const workbook = XLSX.read(data, {
            type: 'binary',
          })
          const wsname = workbook.SheetNames[0]
          const ws = XLSX.utils.sheet_to_json(workbook.Sheets[wsname], {
            header: 0,
            defval: '',
          })
          const excellist = []

          if (ws.length > 0) {
            for (let i = 0; i < ws.length; i++) {
              excellist.push(ws[i])
            }
          } else {
            showErrorToast(this, 'File không có dữ liệu')
            return
          }

          // Kiểm tra định dạng file
          if (
            !excellist[0].hasOwnProperty('MA_SINH_VIEN')
            || !excellist[0].hasOwnProperty('MA_LOP_HOC_PHAN')
            || !excellist[0].hasOwnProperty('DIEM_THI')
          ) {
            showErrorToast(this, 'File chưa đúng định dạng. File phải có các cột: MA_SINH_VIEN, MA_LOP_HOC_PHAN, DIEM_THI')
            return
          }

          this.dataExcel = []

          for (let k = 0; k < excellist.length; k++) {
            const item = excellist[k]
            const obj = {
              stt: k + 1,
              studentCode: item.MA_SINH_VIEN ? item.MA_SINH_VIEN.toString().trim() : '',
              creditClassCode: item.MA_LOP_HOC_PHAN ? item.MA_LOP_HOC_PHAN.toString().trim() : '',
              score: item.DIEM_THI !== null && item.DIEM_THI !== undefined && item.DIEM_THI !== '' ? parseFloat(item.DIEM_THI) : null,
            }
            this.dataExcel.push(obj)
          }

          this.totalRecords = this.dataExcel.length
          this.serverParams.page = 1
          this.updateDisplayedData()

          showSuccessToast(this, `Đã đọc thành công ${this.totalRecords} dòng dữ liệu`)
        } catch (e) {
          showErrorToast(this, `Lỗi đọc file: ${e.message}`)
        }
      }
      fileReader.readAsBinaryString(files[0])
    },

    resetInputFile() {
      if (this.$refs.inputFileRef) {
        this.$refs.inputFileRef.reset()
      }
    },

    checkDataBeforeImport(dataArray) {
      if (dataArray.length === 0) {
        showWarningToast(this, 'Chưa có dữ liệu để thực hiện')
        return false
      }

      const studentCreditClassMap = new Set()

      for (const item of dataArray) {
        const { studentCode, creditClassCode, score } = item

        // Kiểm tra các trường không được rỗng
        if (!studentCode || !creditClassCode) {
          showWarningToast(this, 'Các trường [MA_SINH_VIEN, MA_LOP_HOC_PHAN] không được trống')
          return false
        }

        // Kiểm tra điểm thi
        if (score !== null && score !== undefined && score !== '') {
          const scoreValue = parseFloat(score)
          if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 10) {
            showWarningToast(this, `Điểm thi của sinh viên ${studentCode} không hợp lệ. Điểm phải từ 0 đến 10`)
            return false
          }
        }

        // Kiểm tra trùng lặp
        const key = `${studentCode}_${creditClassCode}`
        if (studentCreditClassMap.has(key)) {
          showWarningToast(this, `Sinh viên ${studentCode} có điểm thi trùng lặp cho lớp học phần ${creditClassCode}`)
          return false
        }
        studentCreditClassMap.add(key)
      }
      return true
    },

    async importData() {
      const data = this.dataExcel
      if (this.checkDataBeforeImport(data)) {
        try {
          this.loading = true
          const res = await api.importExaminationScores(data)
          if (res.code === ApiCode.SUCCESS) {
            showSuccessToast(this, res.message)
            this.resetData()
          } else {
            showErrorToast(this, res.message || 'Có lỗi xảy ra khi import điểm thi')
          }
        } catch (e) {
          showErrorToast(this, `Lỗi: ${e.message}`)
        } finally {
          this.loading = false
        }
      }
    },

    // Helper function to create cell style
    createCellStyle() {
      return {
        borders: {
          color: '#00000000',
          lineStyle: 'thin',
        },
        fontColor: '#ffffff',
        backColor: '#000000',
      }
    },

    // Helper function to create cell
    createCell(index, value) {
      return {
        index,
        value,
        colSpan: 1,
        style: this.createCellStyle(),
      }
    },

    downloadTemplate() {
      try {
        showInfoToast(this, 'Đang tạo file mẫu...')

        const columns = [
          { index: 1, width: 150 },
          { index: 2, width: 200 },
          { index: 3, width: 100 },
        ]

        const cells = [
          this.createCell(1, 'MA_SINH_VIEN'),
          this.createCell(2, 'MA_LOP_HOC_PHAN'),
          this.createCell(3, 'DIEM_THI'),
        ]

        const headerRow = {
          index: 1,
          cells,
        }

        const rows = [headerRow]

        const worksheets = [{ columns, rows }]
        const workbook = new Workbook({ worksheets }, 'xlsx')
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
        const filename = `import_DiemThi_${timestamp}.xlsx`

        workbook.save(filename)

        showSuccessToast(this, 'Tải xuống file mẫu thành công!')
      } catch (error) {
        showErrorToast(this, 'Không thể tạo file mẫu. Vui lòng thử lại.')
      }
    },

    resetData() {
      this.dataExcel = []
      this.table.items = []
      this.totalRecords = 0
      this.tenFile = ''
      this.fileUpload = null
      this.serverParams.page = 1
      this.resetInputFile()
    },

    onPageChange(params) {
      this.serverParams.page = params.currentPage
      this.updateDisplayedData()
    },

    onPerPageChange(params) {
      this.serverParams.perPage = params.currentPerPage
      this.serverParams.page = 1
      this.updateDisplayedData()
    },

    updateDisplayedData() {
      const start = this.serverParams.perPage * (this.serverParams.page - 1)
      const end = this.dataExcel.length > (this.serverParams.perPage * this.serverParams.page)
        ? (this.serverParams.perPage * this.serverParams.page)
        : this.dataExcel.length
      this.table.items = this.dataExcel.slice(start, end)
    },
  },
}
</script>

<style scoped>
.form_file {
  width: auto !important;
}
</style>
